import { useEffect, useRef } from 'react';
import { format, parseISO } from 'date-fns';
import { Calendar, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { trackEvent } from '../../lib/analytics';
import type { CalendarEvent } from '../../types/calendar';

interface TimeTilePopoverProps {
  currentTime: Date;
  upcomingEvents: CalendarEvent[];
  onClose: () => void;
  anchorRef: React.RefObject<HTMLDivElement>;
}

/**
 * TimeTilePopover - Expanded view for the TimeTile component
 *
 * Shows:
 * - Full date (e.g., "Wednesday, January 22, 2026")
 * - Current seconds
 * - Next 3 event titles with times and member colors
 * - Link to calendar view
 *
 * Positioned to the right of the tile (sidebar is on left edge)
 * Closes on outside click or ESC key
 */
export function TimeTilePopover({
  currentTime,
  upcomingEvents,
  onClose,
  anchorRef,
}: TimeTilePopoverProps) {
  const popoverRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        anchorRef.current &&
        !anchorRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    // Handle ESC key
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscKey);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [onClose, anchorRef]);

  const handleEventClick = (event: CalendarEvent) => {
    trackEvent('clock_tile_event_clicked', {
      event_id: event.id,
      member_id: event.member?.id,
    });
    // Could open event preview modal here if needed
  };

  const handleViewCalendar = () => {
    navigate('/calendar');
    onClose();
  };

  const fullDate = format(currentTime, 'EEEE, MMMM d, yyyy');
  const seconds = format(currentTime, 'ss');

  return (
    <div
      ref={popoverRef}
      className="absolute left-[80px] top-0 z-50 w-64 bg-white rounded-xl shadow-lg border border-border-light p-4 animate-fade-in"
      data-testid="time-tile-popover"
    >
      {/* Full date */}
      <div className="flex items-center gap-2 mb-3 pb-3 border-b border-border-light">
        <Calendar className="w-4 h-4 text-teal" />
        <div>
          <div className="text-sm font-medium text-text-primary">{fullDate}</div>
          <div className="text-xs text-text-muted flex items-center gap-1 mt-0.5">
            <Clock className="w-3 h-3" />
            <span>:{seconds} seconds</span>
          </div>
        </div>
      </div>

      {/* Upcoming events */}
      <div className="mb-3">
        <div className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-2">
          Upcoming Events
        </div>
        {upcomingEvents.length === 0 ? (
          <div className="text-sm text-text-muted italic">No upcoming events</div>
        ) : (
          <div className="space-y-2">
            {upcomingEvents.map((event) => {
              const eventStart = parseISO(event.startTime);
              const eventTime = format(eventStart, 'h:mm a');

              return (
                <div
                  key={event.id}
                  onClick={() => handleEventClick(event)}
                  className="flex items-start gap-2 p-2 rounded-lg hover:bg-bg-secondary transition-colors cursor-pointer"
                  data-testid={`popover-event-${event.id}`}
                >
                  {/* Member color dot */}
                  <div
                    className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0"
                    style={{ backgroundColor: event.member?.color || '#15a2a2' }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-text-primary truncate">
                      {event.title}
                    </div>
                    <div className="text-xs text-text-muted">{eventTime}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* View Calendar link */}
      <button
        onClick={handleViewCalendar}
        className="w-full py-2 px-3 text-sm font-medium text-teal hover:bg-teal/10 rounded-lg transition-colors"
        data-testid="view-calendar-button"
      >
        View Calendar →
      </button>
    </div>
  );
}
